/**
 * @module tree/defined
 */

/**
 * @typedef {Element|Text} Target
 */

import never from '@mailobj-browser/front/js/utils/never.js'
import resolvable from '@mailobj-browser/front/js/utils/resolvable.js'

const observers = new WeakMap()
const resolvers = new WeakMap()
const nodes = new Set()

const options = {
  attributes: true
}

const callback = records => {
  for (const { attributeName, target } of records) {
    const { [attributeName]: value } = target
    
    for (const resolve of resolvers.get(target)) {
      resolve(value)
    }
    
    nodes.delete(target)
    resolvers.delete(target)
  }
}

const noop = () => {}

const observing = ownerDocument => {
  if (!observers.has(ownerDocument)) {
    const { defaultView } = ownerDocument
    const { MutationObserver } = defaultView
    const observer = new MutationObserver(callback)
    
    observers.set(ownerDocument, observer)
  }
  
  return observers.get(ownerDocument)
}

/**
 * Awaits the provided attribute defined on the target
 * @param {Target} target The node to be removed
 * @param {string} attribute The attribute
 * @param {?Promise<*>} abort The abortion promise
 * @return {?string}
 */
export default async (target, attribute, abort = never) => {
  const { ownerDocument } = target
  const [defined, { resolve }] = resolvable()
  const observer = observing(ownerDocument)
  
  observer.observe(target, {
    ...options,
    attributeFilter: [attribute]
  })
  
  if (!nodes.has(target)) {
    nodes.add(target)
    resolvers.set(target, new Set())
  }
  
  const resolver = resolvers.get(target)
  
  resolver.add(resolve)
  
  const value = await Promise.race([abort.then(noop), defined])
  
  resolver.delete(resolve)
  
  return value
}
